// Admin → Tags (components/TagsAdminTab.js): the rows of the tags table, the
// tags on live listings plus the saved buyer tags (Reports → Purchases by tag
// reads only those), so a buyer tag no live listing carries can still be
// unticked. Pure; tested in buyerTags.test.js.

const WHITESPACE_RE = /\s+/;
const ASCII_UPPER_RE = /[A-Z]+/g;

// The words of a list of tags: each string entry split on whitespace (a tag is
// one word; an entry may hold several), blanks and non-strings dropped
const wordsOf = (list) => (Array.isArray(list)
  ? list.flatMap(entry => (typeof entry === 'string' ? entry.split(WHITESPACE_RE) : [])).filter(Boolean)
  : []);

// A tag compared without case the way the server does (SQLite NOCASE / LOWER):
// ASCII letters only, so 'BEANS' is 'beans' but 'Émile' is not 'émile'
const foldOf = (tag) => tag.replace(ASCII_UPPER_RE, (s) => s.toLowerCase());

const byFoldThenSpelling = (a, b) => {
  if (a.fold !== b.fold) return a.fold < b.fold ? -1 : 1;
  if (a.name !== b.name) return a.name < b.name ? -1 : 1;
  return 0;
};

/**
 * The Admin Tags table's rows: one per inventory tag spelling (tag colours
 * are kept per exact spelling, so 'Lion' and 'lion' are two rows), plus each
 * saved buyer tag no inventory spelling matches without case (its first
 * spelling). isBuyer compares without case (ASCII only, as the server).
 * Sorted without case, then by spelling.
 * @param {string[]} inventoryTags the tags on live listings (GET /inventory/tags)
 * @param {string[]} buyerTags the saved buyer tags (GET /admin/buyer-tags)
 * @returns {{ name: string, isBuyer: boolean }[]} a new array
 */
export function adminTagRows(inventoryTags, buyerTags) {
  const buyerWords = wordsOf(buyerTags);
  const buyerFolds = new Set(buyerWords.map(foldOf));
  const rows = new Map();
  for (const name of wordsOf(inventoryTags)) {
    if (!rows.has(name)) rows.set(name, { name, fold: foldOf(name) });
  }
  const inventoryFolds = new Set([...rows.values()].map(r => r.fold));
  const buyerOnly = new Map();
  for (const name of buyerWords) {
    const fold = foldOf(name);
    if (!inventoryFolds.has(fold) && !buyerOnly.has(fold)) buyerOnly.set(fold, { name, fold });
  }
  return [...rows.values(), ...buyerOnly.values()]
    .sort(byFoldThenSpelling)
    .map(r => ({ name: r.name, isBuyer: buyerFolds.has(r.fold) }));
}
